import React from 'react';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';
import { Form, Hyperlink } from '@openedx/paragon';
import { selectors } from '../../../../../../data/redux';
import SettingsOption from '../SettingsOption';
import messages from '../messages';
import { scoringCardHooks } from '../hooks';
import { GradingMethod, GradingMethodKeys } from '../../../../../../data/constants/problem';

const ScoringCard = ({
  scoring,
  defaultValue,
  updateSettings,
  // redux
  studioEndpointUrl,
  learningContextId,
  isLibrary,
}) => {
  const intl = useIntl();
  const {
    handleUnlimitedChange,
    handleMaxAttemptChange,
    handleWeightChange,
    handleGradingMethodChange,
    handleOnChange,
    attemptDisplayValue,
  } = scoringCardHooks(scoring, updateSettings, defaultValue);

  const getScoringSummary = (weight, attempts, unlimited, gradingMethod) => {
    let summary = intl.formatMessage(messages.weightSummary, { weight });
    summary += ` ${String.fromCharCode(183)} `;
    summary += unlimited
      ? intl.formatMessage(messages.unlimitedAttemptsSummary)
      : intl.formatMessage(messages.attemptsSummary, { attempts: attempts || defaultValue });

    const methodMessage = GradingMethod[gradingMethod || GradingMethodKeys.LAST_SCORE];

    if (methodMessage) {
      summary += ` ${String.fromCharCode(183)} `;
      summary += intl.formatMessage(messages.gradingMethodSummary, {
        gradingMethod: intl.formatMessage(methodMessage),
      });
    }

    return summary;
  };

  return (
    <SettingsOption
      title={intl.formatMessage(messages.scoringSettingsTitle)}
      summary={getScoringSummary(
        scoring.weight,
        scoring.attempts.number,
        scoring.attempts.unlimited,
        scoring.gradingMethod,
      )}
      className="scoringCard"
    >
      <div className="mb-4">
        <FormattedMessage {...messages.scoringSettingsLabelWithGradingMethod} />
      </div>
      <Form.Group>
        <Form.Control
          as="select"
          value={scoring.gradingMethod || GradingMethodKeys.LAST_SCORE}
          onChange={handleGradingMethodChange}
          floatingLabel={intl.formatMessage(messages.scoringGradingMethodInputLabel)}
        >
          {Object.values(GradingMethodKeys).map((gradingMethod) => {
            const optionDisplayName = GradingMethod[gradingMethod];
            return (
              <option
                key={gradingMethod}
                value={gradingMethod}
              >
                {intl.formatMessage(optionDisplayName)}
              </option>
            );
          })}
        </Form.Control>
        <Form.Control.Feedback>
          <FormattedMessage {...messages.gradingMethodHint} />
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group>
        <Form.Control
          type="number"
          min={0}
          step={0.1}
          value={scoring.weight}
          onChange={handleWeightChange}
          floatingLabel={intl.formatMessage(messages.scoringWeightInputLabel)}
        />
        <Form.Control.Feedback>
          <FormattedMessage {...messages.weightHint} />
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group>
        <Form.Control
          type="number"
          min={0}
          value={attemptDisplayValue}
          onChange={handleOnChange}
          onBlur={handleMaxAttemptChange}
          floatingLabel={intl.formatMessage(messages.scoringAttemptsInputLabel)}
          disabled={scoring.attempts.unlimited}
        />
        <Form.Control.Feedback>
          <FormattedMessage {...messages.attemptsHint} />
        </Form.Control.Feedback>
        <Form.Checkbox
          className="mt-3 decoration-control-label"
          checked={scoring.attempts.unlimited}
          onChange={handleUnlimitedChange}
          disabled={!isNil(defaultValue)}
        >
          <div className="x-small">
            <FormattedMessage {...messages.unlimitedAttemptsCheckboxLabel} />
          </div>
        </Form.Checkbox>
      </Form.Group>
      {!isLibrary && (
        <Hyperlink
          destination={`${studioEndpointUrl}/settings/advanced/${learningContextId}#max_attempts`}
          target="_blank"
        >
          <FormattedMessage {...messages.advancedSettingsLinkText} />
        </Hyperlink>
      )}
    </SettingsOption>
  );
};

ScoringCard.propTypes = {
  // eslint-disable-next-line
  scoring: PropTypes.any.isRequired,
  updateSettings: PropTypes.func.isRequired,
  defaultValue: PropTypes.number,
  // redux
  studioEndpointUrl: PropTypes.string.isRequired,
  learningContextId: PropTypes.string,
  isLibrary: PropTypes.bool.isRequired,
};

ScoringCard.defaultProps = {
  learningContextId: null,
  defaultValue: null,
};

export const mapStateToProps = (state) => ({
  studioEndpointUrl: selectors.app.studioEndpointUrl(state),
  learningContextId: selectors.app.learningContextId(state),
  isLibrary: selectors.app.isLibrary(state),
});

export const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ScoringCard);
